import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Heading,
  Stack,
  Text,
  Flex,
  Badge,
  Input,
  FormControl,
  FormLabel,
  useToast
} from '@chakra-ui/react';
import { userService, obtenerExpedientes } from '../config/index';
import { browserAuthService } from '../services/browserAuth';

/**
 * Pantalla para probar la conexión a Convex y los servicios (usuarios, expedientes, auth).
 * Prueba de conexión y servicios Convex.
 */
const TestConvex: React.FC = () => {
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [users, setUsers] = useState<Array<{ email: string; role: string }>>([]);
  const [expedientes, setExpedientes] = useState<Array<{ id: string; estado?: string; departamento?: string }>>([]);
  const [email, setEmail] = useState('admin@odat-ciudalia.es');
  const [password, setPassword] = useState('Admin123!');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState<{ email?: string; role?: string } | null>(null);

  const toast = useToast();

  const testConnection = async () => {
    try {
      setIsConnected(null);
      setConnectionError(null);
      await userService.getActiveUsersCount();
      setIsConnected(true);
      toast({
        title: 'Conexión exitosa',
        description: 'La conexión a Convex se ha establecido correctamente.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      setIsConnected(false);
      setConnectionError(error instanceof Error ? error.message : 'Error desconocido');
      toast({
        title: 'Error de conexión',
        description: `No se pudo conectar a Convex: ${error instanceof Error ? error.message : 'Error desconocido'}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const loadUsers = async () => {
    try {
      const list = await userService.getAllUsers();
      setUsers(list.map((u) => ({ email: u.email, role: u.role })));
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los usuarios',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const loadExpedientes = async () => {
    try {
      const list = await obtenerExpedientes(10, 0);
      setExpedientes(list.map((e) => ({ id: e.id, estado: e.estado, departamento: e.Departamento })));
    } catch (error) {
      console.error('Error al cargar expedientes:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los expedientes',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const login = async () => {
    try {
      const result = await browserAuthService.login(email, password);
      if (result?.user) {
        setIsLoggedIn(true);
        setCurrentUser(result.user);
        toast({
          title: 'Sesión iniciada',
          description: `Bienvenido, ${result.user.email}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Error de inicio de sesión',
          description: 'Credenciales incorrectas',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: `Error al iniciar sesión: ${error instanceof Error ? error.message : 'Error desconocido'}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const logout = () => {
    browserAuthService.logout();
    setIsLoggedIn(false);
    setCurrentUser(null);
    toast({
      title: 'Sesión cerrada',
      description: 'Has cerrado sesión correctamente',
      status: 'info',
      duration: 5000,
      isClosable: true,
    });
  };

  useEffect(() => {
    const checkAuth = async () => {
      const user = await browserAuthService.getCurrentUser();
      if (user) {
        setIsLoggedIn(true);
        setCurrentUser(user);
      }
    };
    checkAuth();
  }, []);

  return (
    <Container maxW="container.lg" py={8}>
      <Heading mb={6}>Prueba de Convex</Heading>

      <Card mb={6}>
        <CardHeader>
          <Heading size="md">Estado de conexión</Heading>
        </CardHeader>
        <CardBody>
          <Stack spacing={4}>
            <Flex alignItems="center">
              <Text mr={2}>Estado:</Text>
              {isConnected === null ? (
                <Badge>No comprobado</Badge>
              ) : isConnected ? (
                <Badge colorScheme="green">Conectado</Badge>
              ) : (
                <Badge colorScheme="red">Error de conexión</Badge>
              )}
            </Flex>
            {connectionError && <Text color="red.500">Error: {connectionError}</Text>}
            <Button colorScheme="blue" onClick={testConnection}>
              Probar conexión
            </Button>
          </Stack>
        </CardBody>
      </Card>

      <Card mb={6}>
        <CardHeader>
          <Heading size="md">Autenticación</Heading>
        </CardHeader>
        <CardBody>
          {isLoggedIn ? (
            <Stack spacing={4}>
              <Text>Usuario actual: <strong>{currentUser?.email}</strong></Text>
              <Text>Rol: <Badge colorScheme="purple">{currentUser?.role}</Badge></Text>
              <Button colorScheme="red" onClick={logout}>Cerrar sesión</Button>
            </Stack>
          ) : (
            <Stack spacing={4}>
              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Contraseña</FormLabel>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
              </FormControl>
              <Button colorScheme="blue" onClick={login}>Iniciar sesión</Button>
              <Text fontSize="sm">
                Credenciales de prueba: admin@odat-ciudalia.es / Admin123!
              </Text>
            </Stack>
          )}
        </CardBody>
      </Card>

      <Flex gap={6} flexDir={{ base: 'column', md: 'row' }}>
        <Card flex={1}>
          <CardHeader>
            <Heading size="md">Usuarios</Heading>
          </CardHeader>
          <CardBody>
            <Button colorScheme="blue" mb={4} onClick={loadUsers} isDisabled={!isConnected}>
              Cargar usuarios
            </Button>
            {users.length > 0 ? (
              <Stack spacing={3}>
                {users.map((user, index) => (
                  <Box key={index} p={3} borderWidth="1px" borderRadius="md">
                    <Text><strong>Email:</strong> {user.email}</Text>
                    <Text><strong>Rol:</strong> {user.role}</Text>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Text>No hay usuarios para mostrar</Text>
            )}
          </CardBody>
        </Card>
        <Card flex={1}>
          <CardHeader>
            <Heading size="md">Expedientes</Heading>
          </CardHeader>
          <CardBody>
            <Button colorScheme="blue" mb={4} onClick={loadExpedientes} isDisabled={!isConnected}>
              Cargar expedientes
            </Button>
            {expedientes.length > 0 ? (
              <Stack spacing={3}>
                {expedientes.map((exp, index) => (
                  <Box key={index} p={3} borderWidth="1px" borderRadius="md">
                    <Text><strong>ID:</strong> {exp.id}</Text>
                    <Text><strong>Estado:</strong> {exp.estado}</Text>
                    <Text><strong>Departamento:</strong> {exp.departamento}</Text>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Text>No hay expedientes para mostrar</Text>
            )}
          </CardBody>
        </Card>
      </Flex>
    </Container>
  );
};

export default TestConvex;
